import {
  CLAIM_DEPARTMENT_LABELS,
  CLAIM_EVENT_TYPE_LABELS,
  claimPhaseLabel,
  type ClaimDepartment,
} from '@evogard/domain';

/**
 * O vocabulário do sinistro na tela — o texto de cada valor gravado.
 *
 * Os valores são códigos em português, gravados (`colisao`, `sim`,
 * `residencia_do_associado`); o texto que a pessoa lê é daqui. As opções vêm do
 * formulário de abertura do pipe "1 - Aviso de Sinistro" (`notice.json`),
 * com o código escrito a partir do rótulo do Pipefy.
 */

export type Option<T extends string = string> = { value: T; label: string };

export const YES_NO: Option<'sim' | 'nao'>[] = [
  { value: 'sim', label: 'Sim' },
  { value: 'nao', label: 'Não' },
];

/* Os nomes moram em `@evogard/domain` desde 28/10/2026 — o Financeiro também os lê. */
export const CLAIM_TYPE_OPTIONS: Option[] = Object.entries(CLAIM_EVENT_TYPE_LABELS).map(
  ([value, label]) => ({ value, label }),
);

export const CAMERAS_OPTIONS: Option<'sim' | 'nao' | 'nao_foi_possivel_verificar'>[] = [
  { value: 'sim', label: 'Sim' },
  { value: 'nao', label: 'Não' },
  { value: 'nao_foi_possivel_verificar', label: 'Não foi possível verificar' },
];

export const CAUSED_BY_OPTIONS: Option<'associado' | 'terceiro' | 'outro'>[] = [
  { value: 'associado', label: 'Declarante associado' },
  { value: 'terceiro', label: 'Terceiro' },
  { value: 'outro', label: 'Outro' },
];

/* A lista nova de 21/10/2026: os códigos de antes ficam, e entram as duas oficinas. */
export const TOW_DESTINATION_OPTIONS: Option<
  | 'base_do_prestador'
  | 'residencia_do_associado'
  | 'oficina_credenciada'
  | 'oficina_indicada_pelo_cliente'
  | 'outro_local_indicado_pelo_associado'
>[] = [
  { value: 'base_do_prestador', label: 'Base do Prestador do Reboque' },
  { value: 'residencia_do_associado', label: 'Residência do Associado' },
  { value: 'oficina_credenciada', label: 'Oficina Credenciada' },
  { value: 'oficina_indicada_pelo_cliente', label: 'Oficina Indicada Pelo Cliente' },
  {
    value: 'outro_local_indicado_pelo_associado',
    label: 'Outro local indicado pelo Associado',
  },
];

export const REPORTED_THIRD_PARTIES_OPTIONS: Option<
  'sim' | 'nao' | 'sim_mas_nao_identificado'
>[] = [
  { value: 'sim', label: 'Sim' },
  { value: 'nao', label: 'Não' },
  { value: 'sim_mas_nao_identificado', label: 'Sim, mas não identificado' },
];

export const THIRD_PARTY_DAMAGE_OPTIONS: Option[] = [
  { value: 'colisao_contra_muros_e_paredes', label: 'Colisão contra muros e paredes' },
  { value: 'danos_a_portoes_e_grades', label: 'Danos a portões e grades' },
  {
    value: 'colisao_contra_postes_rede_eletrica',
    label: 'Colisão contra postes (rede elétrica)',
  },
  {
    value: 'danos_a_sinalizacao_e_mobiliario_urbano',
    label: 'Danos a sinalização e mobiliário urbano',
  },
  { value: 'vitrines_e_fachadas_comerciais', label: 'Vitrines e fachadas comerciais' },
];

export const STATES = [
  'AC',
  'AL',
  'AM',
  'AP',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MG',
  'MS',
  'MT',
  'PA',
  'PB',
  'PE',
  'PI',
  'PR',
  'RJ',
  'RN',
  'RO',
  'RR',
  'RS',
  'SC',
  'SE',
  'SP',
  'TO',
] as const;

/** O texto de um valor gravado. O valor desconhecido aparece CRU, e não some — a tela não inventa. */
export function labelOf(options: Option[], value: string | null | undefined): string {
  if (value == null || value === '') return '';
  return options.find((o) => o.value === value)?.label ?? value;
}

export const DEPARTMENT_LABEL: Readonly<Record<ClaimDepartment, string>> =
  CLAIM_DEPARTMENT_LABELS;

export const DEPARTMENT_OPTIONS: Option<ClaimDepartment>[] = (
  Object.keys(DEPARTMENT_LABEL) as ClaimDepartment[]
).map((value) => ({ value, label: DEPARTMENT_LABEL[value] }));

/**
 * O nome de cada fase, como o Pipefy a chama. **Os três departamentos leem o
 * catálogo**, que casa por CÓDIGO — até 05/10/2026 a Análise casava por
 * posição contra a lista de fases, e um sinistro em `sindicancia` aparecia
 * como "Aprovado para Reparo". Desde 21/10/2026 a escolha do catálogo mora em
 * `claimPhaseLabel` (`@evogard/domain`), que a Agenda de clientes também lê.
 */
export function phaseLabel(department: ClaimDepartment, phase: string): string {
  return claimPhaseLabel(department, phase);
}
